import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { TrendingUp, Layers, Zap, Globe, Target, CheckCircle, ArrowUpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { stats, challenges, processSteps, features } from '../mockData';
import InfiniteCarousel from '../components/InfiniteCarousel';

const Home = () => {
  const navigate = useNavigate();

  const handleInstantFunding = () => {
    // Set the sub-tab preference in sessionStorage
    sessionStorage.setItem('fxSubTab', 'instant');
    navigate('/get-funded');
  };
  const iconMap = {
    'trending-up': TrendingUp,
    'layers': Layers,
    'zap': Zap,
    'globe': Globe,
    'target': Target,
    'check-circle': CheckCircle,
    'arrow-up-circle': ArrowUpCircle
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section
        className="relative min-h-screen flex items-center justify-center text-white overflow-hidden pt-24 md:pt-20"
      >
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage:
              'url(https://images.unsplash.com/photo-1658065969999-38bf31a5380f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbHV4dXJ5JTIwYWJzdHJhY3QlMjBnb2xkfGVufDB8fHxibGFja3wxNzYyMDI5OTM3fDA&ixlib=rb-4.1.0&q=85)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            filter: 'hue-rotate(140deg) saturate(1.5)'
          }}
        />
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(rgba(10, 10, 10, 0.7), rgba(10, 10, 10, 0.85))'
          }}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 animate-fade-in">
            Trade with Institutional Capital
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto">
            Keep up to 90% of your profits
          </p>
          <p className="text-lg md:text-xl mb-12 max-w-2xl mx-auto opacity-90">
            Choose your challenge or skip right to instant funding. Get funded fast and trade like a pro.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-funded">
              <Button size="lg" className="bg-[#10dfb2] text-black hover:bg-[#10dfb2] font-semibold text-lg px-8 py-6 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-[#10dfb2]/50">
                Start Challenge
              </Button>
            </Link>
            <Button 
              size="lg" 
              onClick={handleInstantFunding}
              className="!bg-gray-300 text-black hover:!bg-gray-400 font-semibold text-lg px-8 py-6 transition-all duration-300 hover:scale-105 hover:shadow-lg border-2 border-[#10dfb2]"
            >
              Tap for Instant Funding
            </Button>
          </div>
          <div className="mt-16 flex flex-wrap justify-center gap-8 text-sm md:text-base">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} />
              <span>Payouts on demand coming soon</span>
            </div>
            {/*
            <div className="flex items-center gap-2">
              <CheckCircle size={20} />
              <span>{stats.payouts} payouts</span>
            </div>
            */}
            <div className="flex items-center gap-2">
              <CheckCircle size={20} />
              <span>{stats.instruments} tradable instruments</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
              Why Choose Funded Lab?
            </h2>
            <img 
              src="/hero-section.png" 
              alt="Brewing Brilliance in Every Trade" 
              className="w-full max-w-4xl mx-auto rounded-2xl shadow-lg"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature) => {
              const Icon = iconMap[feature.icon];
              return (
                <Card
                  key={feature.id}
                  className="border-none shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
                >
                  <CardContent className="p-6 text-center">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1A1A1A] rounded-full mb-4">
                      <Icon className="text-[#10dfb2]" size={32} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section - Auto Scrolling - Show exactly 3 on desktop */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600">
              Four simple steps to start trading with our capital
            </p>
          </div>
        </div>
        <InfiniteCarousel
          items={processSteps}
          itemWidth={352}
          gap={24}
          speedSeconds={30}
          className="w-[352px] md:w-[728px] lg:w-[1104px]"
          renderItem={(step, index) => {
            const Icon = iconMap[step.icon];
            return (
              <div>
                <div className="bg-white rounded-3xl shadow-aqua hover:shadow-aqua-lg transition-all duration-300 p-8 h-full">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#10dfb2] text-black rounded-full mb-4 font-bold text-2xl">
                    {(index % 4) + 1}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </div>
            );
          }}
        />
      </section>

      {/* Programs Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Choose Your Challenge
            </h2>
            <p className="text-xl text-gray-600">
              Select the account size that fits your trading style
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {challenges.map((challenge) => (
              <Card
                key={challenge.id}
                className={`border-2 hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 ${
                  challenge.featured ? 'border-[#10dfb2] shadow-xl' : 'border-gray-200'
                }`}
              >
                <CardContent className="p-6">
                  {challenge.featured && (
                    <div className="bg-[#10dfb2] text-black text-sm font-bold py-1 px-3 rounded-full inline-block mb-4">
                      MOST POPULAR
                    </div>
                  )}
                  <h3 className="text-3xl font-bold text-[#10dfb2] mb-2">
                    {challenge.size}
                  </h3>
                  <p className="text-2xl font-bold text-gray-900 mb-6">{challenge.cost}</p>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Profit Target:</span>
                      <span className="font-semibold">{challenge.profitTarget}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Max Drawdown:</span>
                      <span className="font-semibold">{challenge.maxDrawdown}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Daily Loss:</span>
                      <span className="font-semibold">{challenge.dailyLoss}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Profit Split:</span>
                      <span className="font-semibold text-[#10dfb2]">{challenge.profitSplit}</span>
                    </div>
                  </div>
                  <Link to="#">
                    <Button
                      className={`w-full transition-all duration-300 hover:shadow-lg ${
                        challenge.featured
                          ? 'bg-[#10dfb2] hover:bg-[#10dfb2] text-black font-semibold hover:shadow-[#10dfb2]/50'
                          : 'bg-gray-900 hover:bg-gray-800 text-white'
                      }`}
                    >
                      Get Started
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#10dfb2] to-[#10dfb2] text-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Get Funded?
          </h2>
          <p className="text-xl mb-8">
            Start your journey to becoming a professional funded trader today
          </p>
          <Link to="/get-funded">
            <Button
              size="lg"
              className="bg-black text-[#10dfb2] hover:bg-gray-900 font-semibold text-lg px-12 py-6 transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              Choose Your Program
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
